import React, { useRef, useLayoutEffect, useState, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { IDropdownItem } from "./dropdownUtils";
import { useTranslation } from "react-i18next";
import Toggle from "../option/types/toggle/Toggle";
import Checkbox from "../option/types/checkbox/Checkbox";
import Radio from "../option/types/radio/Radio";

interface NestedDropdownProps {
    items: IDropdownItem[];
    selectedItem: IDropdownItem | null;
    openPath: string[];
    onTogglePath: (path: string[]) => void;
    onItemClick: (item: IDropdownItem) => void;
    level?: number;
    zIndex?: number;
    itemTranslation?: string;
    dropdownId: string;
}

type HSide = "right" | "left";

const VIEW_PAD = 8;
const GAP = 4;

const Submenu: React.FC<{
    parentLiRef: React.RefObject<HTMLLIElement>;
    zIndex: number;
    children: React.ReactNode;
    dropdownId: string;
}> = ({ parentLiRef, zIndex, children, dropdownId }) => {
    const menuRef = useRef<HTMLUListElement>(null);
    const [style, setStyle] = useState<CSSProperties>({});
    const [side, setSide] = useState<HSide>("right");

    const position = () => {
        const li = parentLiRef.current;
        const menu = menuRef.current;
        if (!li || !menu) return;

        const prevVis = menu.style.visibility;
        menu.style.visibility = "hidden";
        menu.style.maxHeight = "none";

        const liRect = li.getBoundingClientRect();
        const menuWidth = menu.offsetWidth;
        const menuHeight = menu.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const spaceRight = vw - liRect.right - GAP;
        const spaceLeft = liRect.left - GAP;
        const useRight: HSide = spaceRight >= menuWidth || spaceRight >= spaceLeft ? "right" : "left";

        let left = useRight === "right" ? liRect.right + GAP : liRect.left - GAP - menuWidth;

        if (left < VIEW_PAD) left = VIEW_PAD;
        if (left + menuWidth > vw - VIEW_PAD) {
            left = Math.max(VIEW_PAD, vw - VIEW_PAD - menuWidth);
        }

        let top = liRect.top;
        const overflowBottom = top + menuHeight + VIEW_PAD - vh;
        if (overflowBottom > 0) top -= overflowBottom;

        if (top < VIEW_PAD) top = VIEW_PAD;

        const maxHeight = Math.max(120, Math.floor(vh - 2 * VIEW_PAD));

        const nextStyle: CSSProperties = {
            position: "fixed",
            top: Math.round(top),
            left: Math.round(left),
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            zIndex,
            visibility: "visible",
        };

        setSide(useRight);
        setStyle(nextStyle);

        menu.style.visibility = prevVis;
    };

    useLayoutEffect(() => {
        position();
        const onWin = () => position();
        const ro = new ResizeObserver(() => position());
        if (parentLiRef.current) ro.observe(parentLiRef.current);
        if (menuRef.current) ro.observe(menuRef.current);
        window.addEventListener("resize", onWin, { passive: true });
        window.addEventListener("scroll", onWin, { passive: true });
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", onWin);
            window.removeEventListener("scroll", onWin);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const portalTarget = typeof document !== "undefined" ? document.body : null;

    const content = (
        <ul
            ref={menuRef}
            className="userful-nested-dropdown-list userful-submenu"
            style={style}
            data-side={side}
            role="menu"
            data-dropdown-id={dropdownId}
        >
            {children}
        </ul>
    );

    return portalTarget ? createPortal(content, portalTarget) : content;
};

const NestedItem: React.FC<{
    item: IDropdownItem;
    selectedItem: IDropdownItem | null;
    openPath: string[];
    onTogglePath: (path: string[]) => void;
    onItemClick: (item: IDropdownItem) => void;
    level: number;
    zIndex: number;
    itemTranslation?: string;
    dropdownId: string;
}> = ({ item, selectedItem, openPath, onTogglePath, onItemClick, level, zIndex, itemTranslation, dropdownId }) => {
    const { t } = useTranslation();
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    const isOpen = openPath[level] === item.id;
    const isDisabled = !!item.disabled;

    const liRef = useRef<HTMLLIElement>(null);

    const renderIcon = () => {
        const icon = item.icon;
        if (icon === undefined || icon === null) return null;
        if (React.isValidElement(icon)) return icon;
        if (typeof icon === "string" || typeof icon === "number") return icon;
        return <FontAwesomeIcon icon={icon as IconProp} />;
    };
    const iconNode = renderIcon();

    const label = itemTranslation ? t(`${itemTranslation}.${item.title}`, item.title) : t(item.title, item.title);

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;
        if (isOpen) onTogglePath(openPath.slice(0, level));
        else onTogglePath([...openPath.slice(0, level), item.id]);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDisabled) return;
        if (hasChildren) handleToggle(e);
        else onItemClick(item);
    };

    const itemClassName = [
        "userful-nested-dropdown-nested-item",
        selectedItem?.id === item.id ? "userful-nested-dropdown-nested-item-selected" : "",
        isDisabled ? "userful-nested-dropdown-nested-item-disabled" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <>
            <li
                ref={liRef}
                className={itemClassName}
                onClick={handleClick}
                data-disabled={isDisabled || undefined}
                aria-disabled={isDisabled}
            >
                <div className="userful-nested-dropdown-item">
                    <div className="userful-dropdown-item-header">
                        {item.options === "checkbox" && (
                            <Checkbox value={item.value} onClick={handleClick} disabled={isDisabled} />
                        )}
                        {item.options === "radio" && (
                            <Radio value={item.value} onClick={handleClick} disabled={isDisabled} />
                        )}

                        <div className="userful-dropdown-item-header">
                            {iconNode && <div className="userful-dropdown-icon-wrapper">{iconNode}</div>}
                            <h1>{label}</h1>
                        </div>
                    </div>

                    {hasChildren && (
                        <div className="userful-dropdown-icon-wrapper" style={{ marginLeft: 20 }}>
                            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                        </div>
                    )}

                    {item.options === "toggle" && (
                        <div className="userful-dropdown-icon-wrapper" style={{ marginLeft: 20 }}>
                            <Toggle value={item.value} onClick={handleClick} disabled={isDisabled} />
                        </div>
                    )}
                </div>

                {hasChildren && isOpen && item.children && (
                    <Submenu parentLiRef={liRef} zIndex={zIndex + 1} dropdownId={dropdownId}>
                        <NestedDropdown
                            items={item.children}
                            selectedItem={selectedItem}
                            openPath={openPath}
                            onTogglePath={onTogglePath}
                            onItemClick={onItemClick}
                            level={level + 1}
                            zIndex={zIndex + 2}
                            itemTranslation={itemTranslation}
                            dropdownId={dropdownId}
                        />
                    </Submenu>
                )}
            </li>

            {(item.divider || item.devider) && <div className="userful-dropdown-divider" />}
        </>
    );
};

const NestedDropdown: React.FC<NestedDropdownProps> = ({
    items,
    selectedItem,
    openPath,
    onTogglePath,
    onItemClick,
    level = 0,
    zIndex = 1,
    itemTranslation,
    dropdownId,
}) => {
    return (
        <>
            {items.map((item) => (
                <React.Fragment key={item.id}>
                    <NestedItem
                        item={item}
                        selectedItem={selectedItem}
                        openPath={openPath}
                        onTogglePath={onTogglePath}
                        onItemClick={onItemClick}
                        level={level}
                        zIndex={zIndex}
                        itemTranslation={itemTranslation}
                        dropdownId={dropdownId}
                    />
                </React.Fragment>
            ))}
        </>
    );
};

export default NestedDropdown;
